/**
 * The .tablo file format (formerly .elec): a zip archive containing
 *   manifest.json  – { app, formatVersion }
 *   project.json   – the Project
 *   assets/...     – images, PDFs (floor plans), photos
 * Older files are upgraded by `migrations` before validation.
 */
import { strFromU8, strToU8, unzipSync, zipSync, type Zippable } from 'fflate';
import { CURRENT_FORMAT_VERSION } from './project.ts';
import { projectSchema } from './schema.ts';
import type { Project } from './types.ts';

export const FILE_EXTENSION = 'tablo';
/** Extensions the app still opens: files saved under the working name Elec. */
export const OPEN_EXTENSIONS = ['tablo', 'elec'];
export const MIME_TYPE = 'application/vnd.tablo+zip';

export class FileFormatError extends Error {}

type Migration = (raw: Record<string, unknown>) => Record<string, unknown>;

/** migrations[n] upgrades a file from version n to n + 1. */
export const migrations: Record<number, Migration> = {
  // v2 (Phase 1): supply voltage and company in the title block.
  1: (raw) => {
    const meta = (raw.meta ?? {}) as Record<string, unknown>;
    return { ...raw, meta: { supplyVoltage: '230 V', company: '', ...meta } };
  },
  // v3 (Phase 2): protection data lives on the protective device element, not the circuit.
  2: (raw) => {
    const circuits = (raw.circuits ?? {}) as Record<string, Record<string, unknown>>;
    const out: Record<string, unknown> = {};
    for (const [id, c] of Object.entries(circuits)) {
      const { protection: _p, rcd: _r, ...rest } = c;
      out[id] = rest;
    }
    return { ...raw, circuits: out };
  },
  // v4 (Phase 4): revisions and user symbols.
  3: (raw) => ({ revisions: {}, symbols: {}, ...raw }),
  // v5 (Phase 6): site survey.
  4: (raw) => ({ surveyRooms: {}, surveyPoints: {}, surveyBoards: {}, surveyCircuits: {}, ...raw }),
  // v6 (Phase 7): Dutch drawings (meta.locale 'nl', Dutch symbol names). Nothing to convert;
  // the version tells older apps the file needs a newer one.
  5: (raw) => raw,
  // v7 (Phase 7): rule packs. Files before it were all checked against the Belgian RGIE/AREI.
  6: (raw) => {
    const meta = (raw.meta ?? {}) as Record<string, unknown>;
    return { ...raw, meta: { standard: 'rgie', ...meta } };
  },
  // v8: test measurements, inspection reports and remarks, invoices; photos on points.
  7: (raw) => ({ measurements: {}, inspections: {}, remarks: {}, invoices: {}, ...raw }),
  // v9: credit notes, deposit invoices, several VAT rates on one invoice (optional fields of
  // invoices). Nothing to convert; older apps must not read a credit note as an invoice.
  8: (raw) => raw,
  // v10: client sign-offs; blanking plates per board; installation usage and client contact
  // (optional fields). Older apps would drop the sign-offs silently, so they must refuse it.
  9: (raw) => ({ signoffs: {}, ...raw }),
};

export interface ProjectFile {
  project: Project;
  assets: Record<string, Uint8Array>;
}

export function serializeProject(file: ProjectFile): Uint8Array {
  const entries: Zippable = {
    'manifest.json': strToU8(JSON.stringify({ app: 'tablo', formatVersion: file.project.formatVersion })),
    'project.json': strToU8(JSON.stringify(file.project)),
  };
  for (const [name, bytes] of Object.entries(file.assets)) {
    if (name.includes('..') || name.startsWith('/')) throw new FileFormatError(`invalid asset name ${name}`);
    entries[`assets/${name}`] = [bytes, { level: 0 }];
  }
  return zipSync(entries, { level: 6 });
}

export function deserializeProject(bytes: Uint8Array): ProjectFile {
  let files: Record<string, Uint8Array>;
  try {
    files = unzipSync(bytes);
  } catch {
    throw new FileFormatError('not a valid .tablo file (zip expected)');
  }
  const projectBytes = files['project.json'];
  if (!projectBytes) throw new FileFormatError('project.json missing');
  let raw: Record<string, unknown>;
  try {
    raw = JSON.parse(strFromU8(projectBytes));
  } catch {
    throw new FileFormatError('project.json is not valid JSON');
  }
  raw = migrate(raw);
  const parsed = projectSchema.safeParse(raw);
  if (!parsed.success) throw new FileFormatError(`invalid project: ${parsed.error.issues[0]?.message ?? ''}`);
  const assets: Record<string, Uint8Array> = {};
  for (const [name, data] of Object.entries(files)) {
    if (name.startsWith('assets/') && !name.endsWith('/')) assets[name.slice(7)] = data;
  }
  return { project: parsed.data as Project, assets };
}

/** Upgrade and validate a project received as JSON (server storage, cloud API). */
export function upgradeProject(raw: unknown): Project {
  if (!raw || typeof raw !== 'object') throw new FileFormatError('project expected');
  const parsed = projectSchema.safeParse(migrate(raw as Record<string, unknown>));
  if (!parsed.success) throw new FileFormatError(`invalid project: ${parsed.error.issues[0]?.message ?? ''}`);
  return parsed.data as Project;
}

export function migrate(raw: Record<string, unknown>): Record<string, unknown> {
  let v = typeof raw.formatVersion === 'number' ? raw.formatVersion : NaN;
  if (!Number.isInteger(v)) throw new FileFormatError('formatVersion missing');
  if (v > CURRENT_FORMAT_VERSION)
    throw new FileFormatError(`file made with a newer version (format ${v}); please update the app`);
  let out = raw;
  while (v < CURRENT_FORMAT_VERSION) {
    const m = migrations[v];
    if (!m) throw new FileFormatError(`no migration from format ${v}`);
    out = { ...m(out), formatVersion: v + 1 };
    v++;
  }
  return out;
}
